(() => {
  const config = window.SilenceWishlistConfig || {};
  if (!config.enabled) return;

  const toggleSelector = '[data-silence-wishlist-toggle]';
  const isLoggedIn = Boolean(config.loggedIn && config.customerId);

  const openWishlistPage = () => {
    window.location.href = config.pageUrl || '/search?view=wishlist';
  };

  if (!isLoggedIn) {
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest(toggleSelector);
      if (!toggle) return;
      event.preventDefault();
      event.stopPropagation();
      openWishlistPage();
    });
    return;
  }

  const STORAGE_VERSION = 'v1';
  const STORAGE_KEY = `silence_wishlist_${STORAGE_VERSION}_${config.customerId}`;

  const readItems = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((item) => item && item.productId && item.handle);
    } catch (error) {
      return [];
    }
  };

  const writeItems = (items) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent('silence:wishlist-changed', { detail: { items } }));
  };

  const getProductId = (button) => String(button?.dataset.productId || '');

  const getVariantId = (button) => {
    if (button?.dataset.dynamicVariant === 'true') {
      const form = button.closest('form[data-type="add-to-cart-form"]') || button.closest('form');
      const input = form?.querySelector('[name="id"]');
      if (input?.value) return String(input.value);
    }
    return String(button?.dataset.variantId || '');
  };

  const isSaved = (button, items = readItems()) => {
    const productId = getProductId(button);
    return Boolean(productId && items.some((item) => String(item.productId) === productId));
  };

  const syncButton = (button, items = readItems()) => {
    if (!button) return;
    const active = isSaved(button, items);
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    const label = active ? button.dataset.removeLabel : button.dataset.addLabel;
    if (label) button.setAttribute('aria-label', label);
  };

  const syncAll = () => {
    const items = readItems();
    document.querySelectorAll(toggleSelector).forEach((button) => syncButton(button, items));
  };

  const toggleItem = (button) => {
    const productId = getProductId(button);
    const variantId = getVariantId(button);
    const handle = String(button.dataset.productHandle || '');
    if (!productId || !handle) return;

    const items = readItems();
    const index = items.findIndex((item) => String(item.productId) === productId);

    if (index >= 0) {
      items.splice(index, 1);
    } else {
      items.unshift({
        productId,
        variantId,
        handle,
        addedAt: Date.now()
      });
    }

    writeItems(items);
    syncAll();
    renderWishlistPage();
  };

  const money = (cents) => {
    const numeric = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(config.locale || document.documentElement.lang || 'it-IT', {
        style: 'currency',
        currency: config.currency || 'EUR'
      }).format(numeric);
    } catch (error) {
      return `${numeric.toFixed(2)} ${config.currency || 'EUR'}`;
    }
  };

  const productUrl = (handle) => {
    const root = String(config.rootUrl || '/').replace(/\/?$/, '/');
    return `${root}products/${encodeURIComponent(handle)}`;
  };

  const productJsonUrl = (handle) => `${productUrl(handle)}.js`;

  const normalizeImage = (src) => {
    if (!src) return '';
    if (src.startsWith('//')) return `https:${src}`;
    return src;
  };

  const fetchProduct = async (item) => {
    const response = await fetch(productJsonUrl(item.handle), { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Product ${item.handle} not found`);
    const product = await response.json();
    const variants = Array.isArray(product.variants) ? product.variants : [];
    let variant = variants.find((v) => String(v.id) === String(item.variantId));
    if (!variant) variant = variants.find((v) => v.available) || variants[0] || null;
    return { item, product, variant };
  };

  const cardMarkup = ({ item, product, variant }, labels) => {
    const url = productUrl(item.handle);
    const title = escapeHtml(product.title || '');
    const variantTitle = variant && variant.title && variant.title !== 'Default Title' ? escapeHtml(variant.title) : '';
    const image = normalizeImage(variant?.featured_image?.src || product.featured_image || product.images?.[0] || '');
    const price = money(variant?.price ?? product.price ?? 0);
    const available = Boolean(variant?.available);
    const variantId = variant?.id || item.variantId || '';

    return `
      <article class="silence-wishlist-card" data-wishlist-card data-product-id="${escapeHtml(item.productId)}">
        <div class="silence-wishlist-card__media">
          <a class="silence-wishlist-card__image-link" href="${url}" aria-label="${title}">
            ${image ? `<img class="silence-wishlist-card__image" src="${escapeHtml(image)}" alt="${title}" loading="lazy" width="600" height="800">` : ''}
          </a>
          <button
            class="silence-wishlist-card__remove"
            type="button"
            data-silence-wishlist-remove
            data-product-id="${escapeHtml(item.productId)}"
            aria-label="${escapeHtml(labels.remove)}"
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <div class="silence-wishlist-card__body">
          <h2 class="silence-wishlist-card__title"><a href="${url}">${title}</a></h2>
          <p class="silence-wishlist-card__variant"${variantTitle ? '' : ' aria-hidden="true"'}>${variantTitle}</p>
          <p class="silence-wishlist-card__price">${price}</p>
          <button
            class="silence-wishlist-card__add"
            type="button"
            data-silence-wishlist-add-cart
            data-variant-id="${escapeHtml(variantId)}"
            ${available ? '' : 'disabled'}
          >${escapeHtml(available ? labels.add : labels.soldOut)}</button>
        </div>
      </article>`;
  };

  const escapeHtml = (value) => {
    const el = document.createElement('div');
    el.textContent = value == null ? '' : String(value);
    return el.innerHTML;
  };

  let renderToken = 0;
  const renderWishlistPage = async () => {
    const page = document.querySelector('[data-silence-wishlist-page]');
    if (!page) return;

    const grid = page.querySelector('[data-silence-wishlist-grid]');
    const loading = page.querySelector('[data-silence-wishlist-loading]');
    const empty = page.querySelector('[data-silence-wishlist-empty]');
    const error = page.querySelector('[data-silence-wishlist-error]');
    if (!grid || !loading || !empty || !error) return;

    const labels = {
      add: page.dataset.addLabel || 'Aggiungi al carrello',
      adding: page.dataset.addingLabel || 'Aggiunta...',
      added: page.dataset.addedLabel || 'Aggiunto',
      soldOut: page.dataset.soldOutLabel || 'Esaurito',
      remove: page.dataset.removeLabel || 'Rimuovi dalla lista'
    };

    const token = ++renderToken;
    const items = readItems();
    loading.hidden = false;
    grid.hidden = true;
    empty.hidden = true;
    error.hidden = true;

    if (!items.length) {
      loading.hidden = true;
      empty.hidden = false;
      grid.innerHTML = '';
      return;
    }

    const results = await Promise.all(items.map(async (item) => {
      try {
        return await fetchProduct(item);
      } catch (err) {
        return null;
      }
    }));

    if (token !== renderToken) return;

    const valid = results.filter(Boolean);
    const validIds = new Set(valid.map((entry) => String(entry.item.productId)));
    if (valid.length !== items.length) {
      writeItems(items.filter((item) => validIds.has(String(item.productId))));
    }

    loading.hidden = true;

    if (!valid.length) {
      grid.innerHTML = '';
      empty.hidden = false;
      return;
    }

    grid.innerHTML = valid.map((entry) => cardMarkup(entry, labels)).join('');
    grid.hidden = false;
  };

  const removeFromPage = (productId) => {
    const items = readItems().filter((item) => String(item.productId) !== String(productId));
    writeItems(items);
    syncAll();
    renderWishlistPage();
  };

  const addToCart = async (button) => {
    const variantId = Number(button.dataset.variantId);
    if (!variantId || button.disabled) return;

    const page = button.closest('[data-silence-wishlist-page]');
    const original = button.textContent;
    button.disabled = true;
    button.textContent = page?.dataset.addingLabel || 'Aggiunta...';

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const sections = typeof cartDrawer?.getSectionsToRender === 'function'
        ? cartDrawer.getSectionsToRender().map((section) => section.id)
        : [];

      const response = await fetch(config.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections,
          sections_url: window.location.pathname
        })
      });

      const payload = await response.json();
      if (!response.ok || payload.status) throw new Error(payload.description || payload.message || 'Cart error');

      button.textContent = page?.dataset.addedLabel || 'Aggiunto';

      if (cartDrawer && payload.sections && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(payload, true);
      } else if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'silence-wishlist',
          productVariantId: variantId,
          cartData: payload
        });
      } else {
        window.location.href = config.cartUrl || '/cart';
        return;
      }

      window.setTimeout(() => {
        button.textContent = original;
        button.disabled = false;
      }, 1200);
    } catch (error) {
      button.textContent = original;
      button.disabled = false;
      const errorBox = page?.querySelector('[data-silence-wishlist-error]');
      if (errorBox) errorBox.hidden = false;
    }
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest(toggleSelector);
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      toggleItem(toggle);
      return;
    }

    const remove = event.target.closest('[data-silence-wishlist-remove]');
    if (remove) {
      event.preventDefault();
      removeFromPage(remove.dataset.productId);
      return;
    }

    const add = event.target.closest('[data-silence-wishlist-add-cart]');
    if (add) {
      event.preventDefault();
      addToCart(add);
    }
  });

  document.addEventListener('silence:wishlist-button-updated', (event) => {
    syncButton(event.detail?.button);
  });

  window.addEventListener('silence:wishlist-changed', syncAll);
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) {
      syncAll();
      renderWishlistPage();
    }
  });

  const observer = new MutationObserver(() => syncAll());
  observer.observe(document.documentElement, { childList: true, subtree: true });

  syncAll();
  renderWishlistPage();
})();
